import { nanoid, fetch_entries, resolve_link } from '#lib/util.js';
import { untrack } from "svelte";

/**
 * title_level for a title set in the paragraph (body) style. It sits above the heading levels
 * so everything that asks "is there a title?" (`title_level > 0`) keeps working unchanged.
 * Such a title renders as a <p>: it is styled text, not a claim about the document outline.
 */
export const TITLE_LEVEL_PARAGRAPH = 4;

export default class StoryBlock {
  // Design
  color = $state();
  block_spacing = $state(); // 'xs' | 's' | 'm' | 'l' | null (null = use layout default). Custom spacing that overrides the layout default.
  block_spacing_bottom = $state(); // When set, bottom spacing is independent from top. When null, uses block_spacing for both (symmetric).
  track = $state(); // 'inner' | 'extended-1' | 'extended-2' | 'flex' | 'full' | null (null = use layout default). Only honoured for layouts whose group supports an independent track, see get_effective_track().
  track_align = $state(); // 'left' | 'center' | 'right' | null (null = centred). Where the track sits on the page; ignored once the track is too wide to leave slack, see get_effective_track_align().

  // Content Fields
  layout = $state(); // reference id for selected layout
  title = $state(); // optional (but at least one text)
  description = $state(); // optional
  preamble = $state(); // optional
  entry_previews_multiref = $state(); // source prop
  entry_previews = $state(); // derived prop (needs to be updated when entry_previews_multiref changes)
  // Per-placement presentation overrides, sparse, keyed by entry_id (as string):
  //   { "88": { fit: 'contain' | 'cover', focal: [x, y] } }
  // Absent key = use the media entry's own settings (fully back-compatible).
  // Crop/focal are decisions about a SLOT: the same photo can be cropped in a
  // hero here and shown whole in a logo grid elsewhere.
  entry_preview_settings = $state({});
  // Pinned aspect ratio for this block's image slots (e.g. '3/2').
  // null = automatic (average of the images, snapped to a brand ratio).
  aspect_ratio = $state(null);
  // Program layout (148): list the next events from the calendar instead of chosen entries.
  // The server resolves them into program_entry_previews on every load; they are never saved.
  program_automatic = $state(false);
  program_entry_previews = $state([]);
  // Regular dates layout (149): the server lists the regular series on every load; never saved.
  regular_series = $state([]);
  primary_link_href = $state();
  primary_link = $state(); // derived prop (needs to be updated when primary_link_href changes)
  secondary_link_href = $state();
  secondary_link = $state(); // derived prop (needs to be updated when secondary_link_href changes)
  primary_link_title = $state('');
  secondary_link_title = $state('');

  // Level of title (0 = no title, 1 = h1, 2 = h2, 3 = h3, TITLE_LEVEL_PARAGRAPH = body style)
  title_level = $state();

  // Visibility of Fields
  show_links = $state();
  show_description = $state();
  show_preamble = $state();
  
  // Visibility of Entry Previews
  show_entry_preview_images = $state();
  show_entry_preview_preamble = $state();
  show_entry_preview_title = $state();
  show_entry_preview_description = $state();
  show_entry_preview_button = $state();
  entry_preview_button_label = $state();
  
  link_entry_previews = $state();

  // Decorative stickers (stamps/badges) anchored to entry previews or the text fields.
  // Each sticker: {
  //   id: string,                     // stable id (nanoid)
  //   src: string,                    // image url (e.g. '/assets/images/xyz.png')
  //   width: number, height: number,  // intrinsic pixel size (for aspect ratio)
  //   label?: string,                 // optional text overlaid on the sticker (supports \n)
  //   href?: string,                  // optional link (e.g. 'tel:...')
  //   anchor: number | 'text',        // index of the entry preview to anchor to, or 'text' for the text fields
  //   area?: string,                  // anchor point: 'top-left'|'top'|'top-right'|'left'|'center'|'right'|'bottom-left'|'bottom'|'bottom-right' (default 'bottom-right')
  //   size?: number,                  // sticker width in px on desktop (scales down on small screens)
  //   rotate?: number,                // rotation in degrees
  //   offset_x?: number, offset_y?: number // fine-tuning offsets in % of the sticker size
  // }
  stickers = $state([]);

  // Derived
  has_content = $derived(this.title || 
    this.description || 
    this.entry_previews_multiref || 
    this.preamble || 
    (this.primary_link_href && this.primary_link_title) || 
    (this.secondary_link_href && this.secondary_link_title)
  );  
  show_title = $derived(this.title_level > 0);

  // UI-related
  ref = $state(); // this will be the link to the component's instance
  #entry_preview_request = 0;
  
  constructor({ 
    // Meta
    block_id, 
    // Design
    color,
    layout,
    block_spacing,
    block_spacing_bottom,
    track,
    track_align,
    // Content Fields
    title,
    description,
    preamble,
    entry_previews_multiref,
    entry_previews,
    entry_preview_settings,
    aspect_ratio,
    program_automatic,
    program_entry_previews,
    regular_series,
    primary_link_href,
    primary_link,
    secondary_link_href,
    secondary_link,
    primary_link_title,
    secondary_link_title,
    title_level,
    // Visibility of EntryPreview fields
    show_entry_preview_images,
    show_entry_preview_preamble,
    show_entry_preview_title,
    show_entry_preview_description,
    show_entry_preview_button,
    entry_preview_button_label,
    // Enable / Disable link to entry preview
    link_entry_previews,
    // Decorative stickers
    stickers,
  } = {}, parent) {
    // Meta
    this.parent = parent;
    this.block_id = block_id ?? nanoid();
    // Design
    this.color = color ?? ''; // Attention: for broken color ids (e.g. deleted colors), we just ignore them and fallback to the default color
    this.type = 'story';
    this.layout = layout ?? 26; // 1; we now use layout 26 as the default as it lines up with the default menu.
    this.block_spacing = block_spacing ?? null; // null = use layout default
    this.block_spacing_bottom = block_spacing_bottom ?? null; // null = symmetric (uses block_spacing for both)
    this.track = track ?? null; // null = use layout default
    this.track_align = track_align ?? null; // null = centred
    // Content Fields
    this.title = title ?? '';
    this.description = description ?? '';
    this.preamble = preamble ?? '';

    // NOTE: For now we inherit the entry_previews_multiref from the actually resolved entry_previews.
    // This auto-cleans up the array which is good, but it also leads to edge cases we need to solve,
    // see NOTE a few lines below. 
    // this.entry_previews_multiref = entry_previews_multiref ? JSON.parse(JSON.stringify(entry_previews_multiref)) : [];
    
    if (entry_previews?.length > 0) {
      // Q: why is $state.snapshot needed here?
      this.entry_previews = $state.snapshot(entry_previews);
      // We assume that only resolvable entries are in the entry_previews array, so we auto-correct that source field here
      // NOTE: There's an edge case we don't cover: If a user sets visibility of an entry to internal, but embeds private entries
      // in a story block, then if another admin edits and saves the entry the links to the private entries will be gone. :(
      // I think for now we can live with this glitch for now, but we should think about a solution for this.
      this.entry_previews_multiref = entry_previews.map(entry => entry.entry_id);
    } else {
      // If only entry_previews_multi_ref is set and no entry_previews are given in the constructor, we use entry_previews_multi_ref
      // This happens during an import script for instance.
      this.entry_previews_multiref = entry_previews_multiref && !entry_previews ? entry_previews_multiref : [];
      this.entry_previews = [];
    }

    this.entry_preview_settings = entry_preview_settings ?? {};
    this.aspect_ratio = aspect_ratio ?? null;
    this.program_automatic = program_automatic === true;
    this.program_entry_previews = Array.isArray(program_entry_previews) ? $state.snapshot(program_entry_previews) : [];
    this.regular_series = Array.isArray(regular_series) ? $state.snapshot(regular_series) : [];

    this.primary_link_href = primary_link_href ?? '';
    this.primary_link = primary_link ?? { type: 'invalid' }; // it should never happen that primary_link is undefined
    this.secondary_link_href = secondary_link_href ?? '';
    this.secondary_link = secondary_link ?? { type: 'invalid' }; // it should never happen that seconary_link is undefined
    this.primary_link_title = primary_link_title ?? '';
    this.secondary_link_title = secondary_link_title ?? '';

    this.title_level = title_level ?? 0;

    // Visibility of Fields
    this.show_links = primary_link_href !== undefined || secondary_link_href !== undefined || primary_link_title !== undefined || secondary_link_title !== undefined;
    this.show_description = description !== undefined; // show_description ?? true;
    this.show_preamble = preamble !== undefined;

    // Visibility of Entry Previews (Embeds)
    // Set defaults for new StoryBlocks, but allow explicit overrides
    this.show_entry_preview_images = show_entry_preview_images ?? true;
    this.show_entry_preview_preamble = show_entry_preview_preamble ?? false;
    this.show_entry_preview_title = show_entry_preview_title ?? false;
    this.show_entry_preview_description = show_entry_preview_description ?? false;
    this.show_entry_preview_button = show_entry_preview_button ?? false;
    this.entry_preview_button_label = entry_preview_button_label ?? '';

    this.link_entry_previews = link_entry_previews ?? true;

    this.stickers = Array.isArray(stickers) ? stickers : [];

    // TODO: potentially check if the color still exists in site_settings.colors (then set this.color to undefined)

    this.cleanup_effects = $effect.root(() => {
      $effect.pre(() => {
        // This read will trigger the effect (note that $state.snapshot is needed,
        // because this.entry_previews_multiref gets mutated but not re-assigned)
        const entry_previews_multiref = $state.snapshot(this.entry_previews_multiref);

        // The logic we do inside untrack to not trigger additional runs
        untrack(() => {
          // Detect if the entry_previews_multiref has changed (deep array comparison needed here)
          if (JSON.stringify(entry_previews_multiref) !== JSON.stringify(this.entry_previews.map(entry => entry.entry_id))) {
            this.refresh_entry_previews();
          }
        });
      });

      // Resolve primary link as it changes
      $effect.pre(() => {
        const href = this.primary_link_href; // read the value to trigger the effect
        untrack(() => {
          if (href && href !== this.primary_link?._href) {
            resolve_link(this.primary_link_href).then(link => {
              this.primary_link = link;
              // console.log('resolved primary link', link);
            });
          }
        });
      });

      // Resolve secondary link as it changes
      $effect.pre(() => {
        const href = this.secondary_link_href; // read the value to trigger the effect
        untrack(() => {
          if (href && href !== this.secondary_link?._href) {
            resolve_link(this.secondary_link_href).then(link => {
              this.secondary_link = link;
              // console.log('resolved secondary link', link);
            });
          }
        });
      });

      return () => {
        // nothing to cleanup yet
      };
    });
  }

  async refresh_entry_previews() {
    const request = ++this.#entry_preview_request;
    const entry_ids = $state.snapshot(this.entry_previews_multiref);
    const entry_previews = await fetch_entries(entry_ids, { as_preview: true });
    const current_ids = $state.snapshot(this.entry_previews_multiref);

    // An insertion or contextual edit may still be resolving when the user moves
    // a preview. Reject superseded requests or changed membership, but apply a
    // refreshed title/image in the current order when only the order changed.
    if (request !== this.#entry_preview_request ||
      JSON.stringify(entry_ids.map(String).sort()) !== JSON.stringify(current_ids.map(String).sort())) return;

    // The endpoint deduplicates IDs. Keep every placement in the current order.
    const previews_by_id = new Map(entry_previews.map(entry => [String(entry.entry_id), entry]));
    this.entry_previews = current_ids.map(id => previews_by_id.get(String(id))).filter(Boolean);
  }

  dispose() {
    this.#entry_preview_request++;
    this.cleanup_effects();
  }

  // EXPERIMENTAL: We currently use this to hide signup teaser blocks, which do not make sense to show when you are logged in.
  // NOTE: We do show the block for admins as it would be surprising for the site owner not to see the site like their public visitors are seeing it.
  is_visible(current_user) {
    if (
      (this.primary_link_href?.startsWith('/login') || this.secondary_link_href?.startsWith('/login')) &&
      current_user && current_user?.role !== 'admin'
    ) {
      return false;
    }
    return true;
  }

  to_json() {
    return {
      block_id: this.block_id,
      type: this.type,
      layout: this.layout,
      color: this.color ? this.color : undefined,
      block_spacing: this.block_spacing ?? undefined, // only save if explicitly set
      block_spacing_bottom: this.block_spacing_bottom ?? undefined, // only save if independently set
      track: this.track ?? undefined, // only save if explicitly set
      track_align: this.track_align ?? undefined, // only save if explicitly set
      
      // Content Fields
      title: this.title_level > 0 ? this.title : undefined,
      description: this.show_description ? this.description : undefined,
      preamble: this.show_preamble ? this.preamble : undefined,
      // Ignore any empty references left in older drafts.
      entry_previews_multiref: this.entry_previews_multiref.filter(Boolean),
      entry_preview_settings: Object.keys(this.entry_preview_settings || {}).length > 0
        ? $state.snapshot(this.entry_preview_settings)
        : undefined,
      aspect_ratio: this.aspect_ratio ?? undefined, // only save if pinned
      program_automatic: this.program_automatic || undefined,
      primary_link_href: this.show_links ? this.primary_link_href : undefined,
      secondary_link_href: this.show_links ? this.secondary_link_href : undefined,
      primary_link_title: this.show_links ? this.primary_link_title : undefined,
      secondary_link_title: this.show_links ?  this.secondary_link_title : undefined,
      title_level: this.title_level > 0 ? this.title_level : undefined,
      
      // Visibility of Entry Previews
      // Only export non-default values to optimize storage and respect explicit settings
      show_entry_preview_images: this.show_entry_preview_images === true ? undefined : this.show_entry_preview_images,
      show_entry_preview_preamble: this.show_entry_preview_preamble || undefined,
      show_entry_preview_title: this.show_entry_preview_title || undefined,
      show_entry_preview_description: this.show_entry_preview_description || undefined,
      show_entry_preview_button: this.show_entry_preview_button || undefined,
      entry_preview_button_label: this.entry_preview_button_label || undefined,

      link_entry_previews: this.link_entry_previews,

      // Decorative stickers (only save when present)
      stickers: this.stickers?.length > 0 ? $state.snapshot(this.stickers) : undefined,
    };
  }

  toJSON() {
    return this.to_json();
  }
}
